import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { EXPONENTIAL_META, type ExponentialParams } from './exponential.model';

/** Exponential params with index signature for JsxParamStore compatibility */
type ExponentialParamsIndexed = ExponentialParams & { [key: string]: number };

/**
 * Creates exponential functions: a * b^(x-h) + k
 *
 * Parameters:
 * - a: vertical scale
 * - b: base (growth/decay factor)
 * - h: horizontal shift
 * - k: vertical shift (horizontal asymptote)
 */
export class ExponentialCreator extends BaseFunctionCreator<ExponentialParamsIndexed> {
  readonly functionType = 'exponential';
  readonly defaultParams: ExponentialParamsIndexed = { ...EXPONENTIAL_META.defaultParams };
  readonly availableFeatures = EXPONENTIAL_META.availableFeatures;
  readonly handleConfigs = EXPONENTIAL_META.handleConfigs;
  override readonly computedValueKeys = ['horizontalAsymptote', 'yIntercept'] as const;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as ExponentialParamsIndexed;

    const colorName = (display?.curve?.color ?? 'purple') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: EXPONENTIAL_META.defaultRanges.a },
      b: { value: initialParams.b, range: EXPONENTIAL_META.defaultRanges.b },
      h: { value: initialParams.h, range: EXPONENTIAL_META.defaultRanges.h },
      k: { value: initialParams.k, range: EXPONENTIAL_META.defaultRanges.k },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const b = paramStore.getValue('b');
      const h = paramStore.getValue('h');
      const k = paramStore.getValue('k');
      return a * Math.pow(b, x - h) + k;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    // Derivative of a * b^(x-h) + k is a * b^(x-h) * ln(b)
    const getDerivative = (order = 1): ((x: number) => number) => {
      return (x: number) => {
        const a = paramStore.getValue('a');
        const b = paramStore.getValue('b');
        const h = paramStore.getValue('h');
        // nth derivative: a * b^(x-h) * (ln(b))^n
        return a * Math.pow(b, x - h) * Math.pow(Math.log(b), order);
      };
    };

    return {
      id,
      type: 'exponential',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as ExponentialParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      getComputedValues: () => ({
        horizontalAsymptote: () => paramStore.getValue('k'),
        yIntercept: () => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const h = paramStore.getValue('h');
          const k = paramStore.getValue('k');
          return a * Math.pow(b, -h) + k;
        },
      }),
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
